import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import {
  rcBatCommandsQuery,
  rcCategoryQuery,
  rcClearStartup,
  rcCustomerConfirmation,
  rcDeviceCommand,
  rcDiagnosticResponse,
  rcInstallMcAfee,
  rcMalwareScan,
  rcOperationsQuery,
  rcResponsesQuery,
  rcRunStandard,
  rcSendBat,
  rcSendOperations,
  rcStandardQuery,
  rcStandardValues,
  rcStartDiagnostic,
  rcUninstallAntivirus,
  type RcCommand,
  type RcDiagnostic,
  type RcStandardValue,
  type RcSubcategory,
  type RcTab,
} from '../api/easeedeskRemoteControl'
import type { ScreenProps } from '../devices/screens'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'

/**
 * Ecranele de categorie ale filei Remote Control a tichetului -
 * `Remote_control::get*Commands` + machetele `remote_control/*_commands.php`
 * (titlul, `ul.nav-tabs.commandsList` cu sub-filele, continutul dedesubt -
 * `RemoteControlCommandsTabs` deschidea prima fila singur).
 *
 * Sub-filele sunt de doua feluri: ecrane ale fisei de aparat (Command
 * Prompt, Active Apps, Startup, OS Updates...) - desenate prin `renderScreen`
 * cu tichetul in `ScreenProps` - si panourile proprii tichetului: AI Diag &
 * Healing (quick diagnostic + user experience + butoanele de masina),
 * Operations (comenzile CMD / BAT / PowerShell -> SITI), Setup / CleanUp
 * (comenzile standard), Other (scripturile .bat) si Commands Responses.
 */

export const REMOTE_CONTROL_CATEGORIES = [
  'getHealthCommands', 'getNetworkCommands', 'getAntivirusCommands', 'getSecurityCommands', 'getEmailCommands',
  'getOfficeCommands', 'getBrowsersCommands', 'getSoftwareCommands', 'getPrintingCommands', 'getPrivacyCommands',
  'getBackupCommands', 'getEvents',
] as const

export function remoteControlCategory(key: string) {
  return function TicketRemoteCategoryScreen(props: ScreenProps) {
    return <TicketRemoteCategory {...props} category={key} />
  }
}

const BOX: CSSProperties = { marginBottom: 10, padding: '10px 20px', borderRadius: 4, border: '1px solid #ddd', borderLeft: '6px solid #DDD', borderRight: '6px solid #DDD' }

function TicketRemoteCategory({ id, ticket, category, screen }: ScreenProps & { category: string }) {
  const ticketId = ticket ?? 0
  const page = useQuery({ ...rcCategoryQuery(ticketId, id, category), enabled: ticketId > 0 })
  const [open, setOpen] = useState<string | null>(null)

  if (ticketId < 1) {
    return (
      <div className="alert alert-info" role="alert">
        <i className="fas fa-info-circle" aria-hidden="true"></i> This panel opens from a ticket.
      </div>
    )
  }
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const tabs = page.data.tabs
  const current = tabs.find((t) => t.key === open) ?? tabs[0]

  return (
    <div className="tk-rc-category">
      <div className="row">
        <div className="col-md-6" style={{ marginTop: 10 }}>
          <h4>{page.data.title}</h4>
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col-md-12">
          <ul className={`nav nav-tabs commandsList-${ticketId}`} role="tablist">
            {tabs.map((tab) => (
              <li key={tab.key} className={current?.key === tab.key ? 'active' : undefined}>
                <a
                  href={`#rc-${tab.key}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setOpen(tab.key)
                  }}
                >
                  <i className={tab.icon} aria-hidden="true"></i> {tab.label}
                </a>
              </li>
            ))}
          </ul>
          <div className={`commands-content-${ticketId}`} style={{ margin: 10 }}>
            {current && <Panel key={current.key} tab={current} ticket={ticketId} device={id} unique={page.data.device.unique} screen={screen} />}
          </div>
        </div>
      </div>
    </div>
  )
}

function Panel({ tab, ticket, device, unique, screen }: { tab: RcTab; ticket: number; device: number; unique: string; screen: ScreenProps['screen'] }) {
  switch (tab.kind) {
    case 'screen':
      return <>{screen(tab.href ?? '', tab.label)}</>
    case 'operations':
      return <OperationsPanel ticket={ticket} device={device} category={tab.category ?? 0} />
    case 'standard':
      return <StandardPanel ticket={ticket} device={device} category={tab.category ?? 0} />
    case 'bat':
      return <BatPanel ticket={ticket} device={device} />
    case 'responses':
      return <ResponsesPanel ticket={ticket} device={device} />
    case 'quick':
      return <QuickPanel ticket={ticket} device={device} diagnostic={tab.diagnostic ?? ''} unique={unique} />
  }
  return null
}

/* ---------------------------------------------------------------------- */
/*  AI Diag & Healing (health_commands/quick_diagnostic.php + general/*)     */
/* ---------------------------------------------------------------------- */

function ago(seconds: number | null): string {
  if (seconds === null) return ''
  if (seconds < 60) return '(updated 1 minute ago)'
  const m = Math.round(seconds / 60)
  return `(updated ${m} minute${m === 1 ? '' : 's'} ago)`
}

function DiagnosticBox({ ticket, device, diagnostic, experience, title }: { ticket: number; device: number; diagnostic: string; experience: boolean; title: string }) {
  const [state, setState] = useState<RcDiagnostic | null>(null)
  const [response, setResponse] = useState<unknown>(null)
  const timer = useRef<number | null>(null)

  const start = async (fresh: boolean) => {
    try {
      const started = await rcStartDiagnostic(ticket, device, diagnostic, experience, fresh)
      setState(started)
      setResponse(started.response)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', 'Error', 'error')
    }
  }

  useEffect(() => {
    void start(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket, device, diagnostic, experience])

  /* Polling-ul din QuickDiagnostic / UserExperience (remote_control.js): pana vine raspunsul. */
  useEffect(() => {
    if (!state || state.action_id < 1 || response !== null) return
    timer.current = window.setInterval(() => {
      void rcDiagnosticResponse(ticket, device, experience, state.action_id).then((r) => {
        if (r.response !== null) setResponse(r.response)
      })
    }, 5000)
    return () => {
      if (timer.current !== null) window.clearInterval(timer.current)
    }
  }, [state, response, ticket, device, experience])

  return (
    <div className="row">
      <div className="col-md-12" style={BOX}>
        <div className="row">
          <h5 className="col-md-9">{title}</h5>
          <button type="button" className="btn btn-sm btn-primary pull-right" disabled={!state} onClick={() => void start(true)} title="Run again">
            <i className="fas fa-sync-alt" aria-hidden="true"></i>
          </button>
          <span className="color-red font-10 pull-right" style={{ padding: 5 }}>
            {ago(state?.updated ?? null)}
          </span>
        </div>
        <div data-action={state?.action_id ?? 0}>
          {!state || state.action_id < 1 ? (
            <em className="text-muted">Not available for this category.</em>
          ) : response === null ? (
            <p className="text-muted">
              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Waiting for the agent...
            </p>
          ) : (
            <RawResponse value={response} />
          )}
        </div>
      </div>
    </div>
  )
}

/** Raspunsul agentului, cheie cu cheie - formatarea pe categorii (QuickDiagnostics/*.php) nu s-a mutat inca. */
function RawResponse({ value }: { value: unknown }) {
  if (value === null || typeof value !== 'object') return <pre style={{ whiteSpace: 'pre-wrap' }}>{String(value)}</pre>
  const entries = Object.entries(value as Record<string, unknown>)
  return (
    <table className="table table-condensed table-striped" style={{ marginBottom: 0 }}>
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td className="td-small" style={{ width: 220 }}>
              <strong>{k}</strong>
            </td>
            <td className="td-small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function QuickPanel({ ticket, device, diagnostic, unique }: { ticket: number; device: number; diagnostic: string; unique: string }) {
  const [busy, setBusy] = useState<string | null>(null)
  const send = async (key: string, run: () => Promise<{ ok: boolean; message?: string }>, done: string, title: string) => {
    if (busy) return
    setBusy(key)
    try {
      const r = await run()
      if (!r.ok) {
        toast(r.message ?? 'Something went wrong.', title, 'error')
        return
      }
      toast(done, title)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', title, 'error')
    } finally {
      setBusy(null)
    }
  }
  const power = (command: 'force-restart' | 'soft-restart' | 'log-off') =>
    void send(command, () => rcDeviceCommand(ticket, device, command), 'Command sent', 'Device Command')

  return (
    <div data-unique={unique}>
      <DiagnosticBox ticket={ticket} device={device} diagnostic={diagnostic} experience={false} title="Quick Diagnostic" />
      {(diagnostic === 'health' || diagnostic === 'network') && (
        <DiagnosticBox ticket={ticket} device={device} diagnostic={diagnostic} experience={true} title="User Experience" />
      )}
      <div className="row">
        <div className="col-md-12" style={BOX}>
          <div className="row">
            <div className="col-md-2">
              <em className="text-muted font-10">Power Shell: see the Power Shell tab.</em>
            </div>
            <div className="col-md-10">
              {diagnostic === 'software' && (
                <button type="button" className="btn btn-danger btn-sm pull-right" style={{ marginLeft: 5 }} disabled={busy !== null} onClick={() => void send('clear', () => rcClearStartup(ticket, device, true), 'Instructions sent', 'Commands')}>
                  Clear Startup Apps
                </button>
              )}
              <button type="button" className="btn btn-danger btn-sm pull-right" disabled={busy !== null} onClick={() => power('force-restart')}>
                <i className="fas fa-power-off" aria-hidden="true"></i> FORCE restart
              </button>
              <button type="button" className="btn btn-warning btn-sm pull-right" style={{ marginRight: 5 }} disabled={busy !== null} onClick={() => power('soft-restart')}>
                <i className="fas fa-undo" aria-hidden="true"></i> Restart
              </button>
              <button type="button" className="btn btn-success btn-sm pull-right" style={{ marginRight: 5 }} disabled={busy !== null} onClick={() => power('log-off')}>
                <i className="fas fa-power-off" aria-hidden="true"></i> Logoff user
              </button>
              <button type="button" className="btn btn-sm btn-danger pull-right" style={{ marginRight: 5 }} disabled={busy !== null} onClick={() => void send('tcc', () => rcCustomerConfirmation(ticket, device), 'Instructions sent', 'Customer Confirmation')}>
                <i className="fa fa-question-circle" aria-hidden="true"></i> Ask for customer confirmation
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------------- */
/*  Operations (general/advanced_commands.php)                             */
/* ---------------------------------------------------------------------- */

function OperationsPanel({ ticket, device, category }: { ticket: number; device: number; category: number }) {
  const [typed, setTyped] = useState('')
  const [search, setSearch] = useState('')
  const [inform, setInform] = useState(true)
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState<RcCommand | null>(null)
  const [argument, setArgument] = useState('')
  const page = useQuery(rcOperationsQuery(ticket, device, category, search))

  if (page.error) return <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
  if (!page.data) return <Spinner />

  const run = async (command: RcCommand, args?: string) => {
    if (busy) return
    setBusy(true)
    try {
      const op: Parameters<typeof rcSendOperations>[2][number] = {
        cmdId: command.id,
        type: command.type,
        informClient: inform ? 1 : 0,
        customerConfirmation: confirm ? 1 : 0,
        source: 1,
        ...(args !== undefined ? { arguments: args } : {}),
      }
      const r = await rcSendOperations(ticket, device, [op])
      if (!r.success) {
        toast('An error ocurred. Please try again later.', 'Commands', 'error')
        return
      }
      toast('Instruction sent', 'Commands')
      setAsking(null)
      setArgument('')
    } catch {
      toast('An error ocurred. Please try again later.', 'Commands', 'error')
    } finally {
      setBusy(false)
    }
  }

  const button = (command: RcCommand) => (
    <span key={command.id}>
      <button
        type="button"
        data-cmd={command.id}
        data-type={command.type}
        className={`btn btn-sm ${command.color ? '' : 'btn-default'}`}
        style={command.color ? { background: command.color, border: command.color, color: '#fff' } : undefined}
        disabled={busy}
        onClick={() => (command.has_parameters ? setAsking(command) : void run(command))}
      >
        {command.name}
        {command.type_name !== '' && (
          <p style={{ marginBottom: 0 }}>
            <em style={{ fontSize: 10 }}>{command.type_name}</em>
          </p>
        )}
      </button>
      {command.info !== '' && (
        <i className="fas fa-info-circle color-blue font-14" title={command.info} aria-hidden="true" style={{ position: 'relative', top: command.type_name ? -18 : -13, left: -10 }}></i>
      )}{' '}
    </span>
  )

  const extras = (sub: RcSubcategory) => (
    <>
      {page.data.antivirus && sub.name.toLowerCase() === 'troubleshoot' && (
        <div>
          <button type="button" className="btn btn-success btn-sm" disabled={busy} onClick={() => void act(() => rcMalwareScan(ticket, device, false))}>
            Quick Malware Scan & Repair
          </button>{' '}
          <button type="button" className="btn btn-success btn-sm" disabled={busy} onClick={() => void act(() => rcMalwareScan(ticket, device, true))}>
            Full Malware Scan & Repair
          </button>
        </div>
      )}
      {page.data.antivirus && sub.name.toLowerCase() === 'setup' && (
        <div>
          <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => void act(() => rcInstallMcAfee(ticket, device))}>
            Install McAfee
          </button>
        </div>
      )}
    </>
  )

  const act = async (fn: () => Promise<{ ok: boolean; message?: string }>) => {
    if (busy) return
    setBusy(true)
    try {
      const r = await fn()
      if (!r.ok) toast(r.message ?? 'Something went wrong.', 'Commands', 'error')
      else toast('Instructions sent', 'Commands')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', 'Commands', 'error')
    } finally {
      setBusy(false)
    }
  }

  const block = (subs: RcSubcategory[], cls: string) =>
    subs
      .filter((s) => s.commands.length > 0)
      .map((sub) => (
        <div className="row" key={`${cls}-${sub.id}`}>
          <div className={`bs-callout ${cls}`}>
            <h5 style={{ marginBottom: 10 }}>{sub.name}</h5>
            <div style={{ marginBottom: 10 }} className="commands-content">
              {sub.commands.map(button)}
              {extras(sub)}
            </div>
          </div>
        </div>
      ))

  const server = page.data.server_subcategories.filter((s) => s.commands.length > 0)
  const all = page.data.subcategories.filter((s) => s.commands.length > 0)

  return (
    <div id={`search-result-container-${ticket}`}>
      <div className="row">
        <div className="col-md-12">
          <form
            className="form-horizontal"
            onSubmit={(e) => {
              e.preventDefault()
              setSearch(typed.trim())
            }}
          >
            <div className="row">
              <div className="col-md-6 input-group">
                <span className="input-group-addon">
                  <i className="fas fa-search" aria-hidden="true"></i>
                </span>
                <input type="text" placeholder="Search..." name="qs" className="form-control input-sm" value={typed} onChange={(e) => setTyped(e.target.value)} />
              </div>
            </div>
          </form>
        </div>
      </div>

      {server.length > 0 && (
        <>
          <div className="row">
            <div>
              <h4 className="bg-success p-1 mt-1">
                <i className="fas fa-server" aria-hidden="true"></i> Server commands
              </h4>
            </div>
          </div>
          {block(server, 'bs-callout-success')}
        </>
      )}

      <div className="row">
        <div>
          <h4 className="bg-info p-1 mt-2">
            <i className="fas fa-desktop" aria-hidden="true"></i> All commands
          </h4>
        </div>
      </div>
      {all.length > 0 && server.length === 0 && (
        <div className="row">
          <div className="col-md-12 p-2">
            <div className="col-md-4">
              <SettingsCheck checked={inform} onChange={setInform} label="Send command response to client" />
            </div>
            <div className="col-md-4">
              <SettingsCheck checked={confirm} onChange={setConfirm} label={<span className="color-red">Wait for customer confirmation</span>} />
            </div>
            <div className="col-md-4">
              <button type="button" className="btn btn-xs btn-danger pull-right" disabled={busy} onClick={() => void act(() => rcCustomerConfirmation(ticket, device))}>
                Ask for customer confirmation
              </button>
            </div>
          </div>
        </div>
      )}
      {block(all, 'bs-callout-info')}

      {all.length === 0 && server.length === 0 && (
        <div className="alert alert-info">
          <i className="fas fa-info-circle" aria-hidden="true"></i> No commands defined.
        </div>
      )}

      {asking && (
        <div className="row">
          <div className="col-md-12" style={BOX}>
            <form
              className="form-inline"
              onSubmit={(e) => {
                e.preventDefault()
                void run(asking, argument)
              }}
            >
              <label style={{ marginRight: 8 }}>{asking.name}</label>
              <input type="text" className="form-control input-sm" value={argument} onChange={(e) => setArgument(e.target.value)} placeholder="Arguments" autoFocus />{' '}
              <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>
                <i className="fa fa-check" aria-hidden="true"></i>
              </button>{' '}
              <button type="button" className="btn btn-sm btn-default" onClick={() => setAsking(null)}>
                <i className="fa fa-times" aria-hidden="true"></i>
              </button>
            </form>
          </div>
        </div>
      )}

      {page.data.antivirus && page.data.uninstallers.length > 0 && (
        <div className="row">
          <div className="col-md-12" style={BOX}>
            <h5 style={{ marginBottom: 10 }}>Uninstall</h5>
            {page.data.uninstallers.map((name) => (
              <div key={name}>
                <button type="button" className="btn btn-success btn-sm" style={{ marginBottom: 4 }} disabled={busy} onClick={() => void act(() => rcUninstallAntivirus(ticket, device, name))}>
                  Uninstall {name.charAt(0).toUpperCase() + name.slice(1)}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------------- */
/*  Setup / CleanUp (standard_commands.php)                                */
/* ---------------------------------------------------------------------- */

function StandardPanel({ ticket, device, category }: { ticket: number; device: number; category: number }) {
  const page = useQuery(rcStandardQuery(ticket, device, category))
  const [values, setValues] = useState<Record<number, RcStandardValue>>({})
  const [picked, setPicked] = useState<Record<number, boolean>>({})
  const [inform, setInform] = useState(true)
  const [busy, setBusy] = useState(false)

  /* checkStandardCommandsValues, la 5 s, pana raspunde agentul. */
  useEffect(() => {
    if (!page.data) return
    let stop = false
    const poll = () =>
      void rcStandardValues(ticket, device, category).then((rows) => {
        if (stop) return
        const next: Record<number, RcStandardValue> = {}
        for (const row of rows) next[row.command_id] = row
        setValues(next)
      })
    poll()
    const t = window.setInterval(poll, 5000)
    return () => {
      stop = true
      window.clearInterval(t)
    }
  }, [page.data, ticket, device, category])

  if (page.error) return <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
  if (!page.data) return <Spinner />

  const commands = page.data.types.flatMap((t) => t.commands)
  const chosen = commands.filter((c) => picked[c.id])

  const run = async (restore: boolean) => {
    if (busy || chosen.length === 0) return
    if (!window.confirm(`Are you sure?\n\nThe selected commands will be ${restore ? 'restored' : 'executed'}.`)) return
    setBusy(true)
    try {
      const map: Record<number, string> = {}
      for (const c of chosen) map[c.id] = c.command
      await rcRunStandard(ticket, device, category, map, restore, inform)
      toast('Instructions sent', 'Commands')
      setPicked({})
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', 'Commands', 'error')
    } finally {
      setBusy(false)
    }
  }

  if (commands.length === 0) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle" aria-hidden="true"></i> No commands defined.
      </div>
    )
  }

  return (
    <div>
      {page.data.types.map((type) => (
        <div className="row" key={type.type_id}>
          <div className="bs-callout bs-callout-info">
            <h5 style={{ marginBottom: 10 }}>{type.type_name}</h5>
            <table className="table table-condensed" style={{ marginBottom: 0 }}>
              <tbody>
                {type.commands.map((c) => {
                  const v = values[c.id]
                  return (
                    <tr key={c.id}>
                      <td className="td-small" style={{ width: 30 }}>
                        <SettingsCheck checked={picked[c.id] ?? false} onChange={(on) => setPicked((was) => ({ ...was, [c.id]: on }))} aria-label={c.title} />
                      </td>
                      <td className="td-small">
                        <strong>{c.title}</strong>
                        {c.info !== '' && <i className="fas fa-info-circle color-blue font-14" title={c.info} aria-hidden="true" style={{ marginLeft: 6 }}></i>}
                      </td>
                      <td className="td-small" style={{ width: 160 }}>
                        {v ? (
                          <span className={`label ${v.new_value === 'on' ? 'label-success' : v.new_value === 'off' ? 'label-danger' : 'label-default'}`}>{v.new_value ?? 'n/a'}</span>
                        ) : (
                          <span className="text-muted font-10">
                            <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> waiting
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <div className="row">
        <div className="col-md-12 p-2">
          <span style={{ marginRight: 12 }}>
            <SettingsCheck checked={inform} onChange={setInform} label="Inform the client" />
          </span>
          <button type="button" className="btn btn-sm btn-primary" disabled={busy || chosen.length === 0} onClick={() => void run(false)}>
            <i className="far fa-play-circle" aria-hidden="true"></i> Execute
          </button>{' '}
          <button type="button" className="btn btn-sm btn-warning" disabled={busy || chosen.length === 0} onClick={() => void run(true)}>
            <i className="fas fa-undo" aria-hidden="true"></i> Restore
          </button>
        </div>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------------- */
/*  Other (health_commands/other_commands.php)                             */
/* ---------------------------------------------------------------------- */

function BatPanel({ ticket, device }: { ticket: number; device: number }) {
  const page = useQuery(rcBatCommandsQuery(ticket, device))
  const [busy, setBusy] = useState(false)
  if (page.error) return <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
  if (!page.data) return <Spinner />

  const send = async (command: string) => {
    if (busy) return
    setBusy(true)
    try {
      const r = await rcSendBat(ticket, device, command)
      if (!r.ok) toast(r.message ?? 'Something went wrong.', 'Commands', 'error')
      else toast('Instruction sent', 'Commands')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', 'Commands', 'error')
    } finally {
      setBusy(false)
    }
  }

  if (page.data.groups.length === 0) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle" aria-hidden="true"></i> No commands defined.
      </div>
    )
  }

  return (
    <div>
      {page.data.groups.map((g) => (
        <div className="row" key={g.details.id}>
          <div className="bs-callout bs-callout-info">
            <h5 style={{ marginBottom: 10 }}>{g.details.name}</h5>
            <div style={{ marginBottom: 10 }} className="commands-content">
              {(g.sub_commands.length > 0 ? g.sub_commands : g.details.command ? [g.details] : []).map((c) => (
                <button key={c.id} type="button" className="btn btn-sm btn-default" style={{ marginRight: 4, marginBottom: 4 }} disabled={busy} onClick={() => void send(c.command)}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------------- */
/*  Commands Responses (events/commands_responses.php)                     */
/* ---------------------------------------------------------------------- */

function ResponsesPanel({ ticket, device }: { ticket: number; device: number }) {
  const page = useQuery(rcResponsesQuery(ticket, device))
  const queryClient = useQueryClient()
  useEffect(() => {
    /* Insigna de pe fila Events se stinge: raspunsurile s-au vazut. */
    void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticket, 'remote-control', device, 'unviewed'] })
  }, [page.data, queryClient, ticket, device])
  if (page.error) return <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
  if (!page.data) return <Spinner />

  return (
    <table className="table table-striped table-hover" width="100%">
      <thead>
        <tr>
          <th className="td-small a-left">Date</th>
          <th className="td-small a-left">Command</th>
          <th className="td-small a-left">Status</th>
          <th className="td-small a-left">Response</th>
          <th className="td-small a-left">User</th>
        </tr>
      </thead>
      <tbody>
        {page.data.rows.length === 0 ? (
          <tr>
            <td colSpan={5} className="dataTables_empty">
              No data available in table
            </td>
          </tr>
        ) : (
          page.data.rows.map((row) => (
            <tr key={row.id} id={`row_${row.id}`}>
              <td className="td-small">{row.created}</td>
              <td className="td-small">
                <strong>{row.command_name}</strong>
                <div className="color-light-grey font-10">{row.command}</div>
              </td>
              <td className="td-small">{row.status}</td>
              <td className="td-small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {row.response}
              </td>
              <td className="td-small">{row.user}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}
